import { useCallback, useEffect, useRef } from 'react';
import ThreeJSApp from '@/shared/lib/threejs/app';
import { AvatarType } from '@/shared/lib/threejs/avatar/common/player/type';
import { useAvatarStore } from '@/shared/store/avatar-store';
import {
  AnimationName,
  UseAvatarAgentReturn
} from '../types/type';
import { useShallow } from 'zustand/react/shallow';
import { getThreeJsConfig, getCustomAvatarData, type DisplayMode } from '../const/config';
import { createCustomAvatarConfig, createCustomAvatarConfigWithScenarios, type CustomAvatarDataWithScenarios } from '@/shared/lib/threejs/config/custom-avatar.config';

//TODO: 렌더링 / 비즈니스 로직이 연결되어있음. (Agent Websocket 비즈니스 로직 / Avatar Render 영역 분리 필요)
//TODO: 애니메이션 시나리오 목록이 외부로 노출될 필요가 있음
export const useAvatarAgent = (app: ThreeJSApp | null, displayMode: DisplayMode = 'web'): UseAvatarAgentReturn => {
  const { completeLoading, isAvatarReady, startLoading } = useAvatarStore(useShallow(state => ({
    isAvatarReady: state.isAvatarReady,
    completeLoading: state.completeLoading,
    startLoading: state.startLoading,
  })));

  const selectAvatar = useCallback(async (avatarType: AvatarType) => {
    if (!app) {
      console.warn('🎭 ThreeJS 앱이 아직 준비되지 않았습니다.');
      return;
    }
    startLoading(avatarType);

    // World가 없거나 destroy된 경우에만 재생성
    if (!app.world || app.world.currentAvatarType === null) {
      app.recreateWorld();
    }


    try {
      // Agent Avatar 설정 생성

      // Three.js 설정 업데이트 (별도 설정 사용)
      const threeJsConfig = getThreeJsConfig(avatarType, displayMode);
      app.updateConfig(threeJsConfig);

      // 커스텀 아바타인 경우 AvatarManager에 config 등록
      if (avatarType === 'custom') {
        const customAvatarData = getCustomAvatarData();
        console.log('🎭 [USE-AVATAR-AGENT] getCustomAvatarData 결과:', customAvatarData);
        console.log('🎭 [USE-AVATAR-AGENT] modelUrl:', customAvatarData?.modelUrl);

        if (customAvatarData) {
          // 시나리오 설정이 있으면 WithScenarios 버전 사용
          const hasScenarioConfigs = 'scenarioConfigs' in customAvatarData &&
            ((customAvatarData as CustomAvatarDataWithScenarios).scenarioConfigs?.length ?? 0) > 0;

          const customAvatarConfig = hasScenarioConfigs
            ? createCustomAvatarConfigWithScenarios(customAvatarData as CustomAvatarDataWithScenarios)
            : createCustomAvatarConfig(customAvatarData);

          console.log('🎭 [USE-AVATAR-AGENT] 시나리오 설정 사용:', hasScenarioConfigs);
          console.log('🎭 [USE-AVATAR-AGENT] 생성된 config:', customAvatarConfig);
          app.world.avatarManager.registerAvatarConfig('custom', customAvatarConfig);
          console.log('🎭 [USE-AVATAR-AGENT] 커스텀 아바타 config 등록 완료');
        } else {
          console.warn('🎭 [USE-AVATAR-AGENT] 커스텀 아바타 데이터가 없습니다. 기본 아바타로 대체합니다.');
          // 커스텀 아바타 데이터가 없으면 기본 아바타(nalnani)로 대체
          await app.world.selectAvatar('nalnani');
          const avatar = app.world.getCurrentAvatar();
          if (avatar) {
            avatar.on('ready', completeLoading);
          }
          return;
        }
      }

      // 아바타 선택 및 로딩 시작
      await app.world.selectAvatar(avatarType);

      const avatar = app.world.getCurrentAvatar();
      if (avatar) {
        avatar.on('ready', completeLoading);
      }
    } catch (error) {
      console.error(`❌ 아바타 ${avatarType} 선택 실패:`, error);
    }
  }, [app, completeLoading, startLoading]);

  // 시나리오별 애니메이션 재생 함수
  const playAnimation = useCallback((animationName: AnimationName) => {
    if (!app?.world || !isAvatarReady) {
      console.warn('🎭 아바타가 아직 준비되지 않았습니다.');
      return;
    }

    app.world.play(animationName);
  }, [isAvatarReady, app]);

  // 아바타 준비 완료 후 기본 애니메이션 재생
  useEffect(() => {
    if (isAvatarReady) {
      playAnimation("toss");
    }
  }, [isAvatarReady, playAnimation]);

  // 애니메이션 중지
  const stopAnimation = useCallback(() => {
    if (app?.world) {
      app.world.stop();
    }
  }, [app]);

  // 립싱크 재생
  const playLipSync = useCallback((blendshapes: number[][], duration?: number) => {
    if (app?.world) {
      app.world.playLipSync(blendshapes, duration);
    }
  }, [app]);

  // 립싱크 중지
  const stopLipSync = useCallback(() => {
    if (app?.world) {
      app.world.stopLipSync();
    }
  }, [app]);

  // 현재 등록된 콜백을 추적하기 위한 ref
  const currentCallbackRef = useRef<((avatarType: AvatarType | null) => void) | null>(null);

  // 아바타 클릭 이벤트 콜백 설정
  const onClickAvatar = useCallback((callback: (avatarType: AvatarType | null) => void) => {
    if (!app) return;

    // 기존 콜백이 있으면 제거
    if (currentCallbackRef.current) {
      app.removeAvatarClickCallback();
    }

    // 새 콜백 등록
    app.setAvatarClickCallback(callback);
    currentCallbackRef.current = callback;
  }, [app]);

  // 컴포넌트 언마운트 시 콜백 정리
  useEffect(() => {
    return () => {
      if (app && currentCallbackRef.current) {
        app.removeAvatarClickCallback();
        currentCallbackRef.current = null;
      }
    };
  }, [app]);

  return {
    playAnimation,
    stopAnimation,
    selectAvatar,
    playLipSync,
    stopLipSync,
    onClickAvatar,
  };
};
